import React from 'react';
import { MapPin, Calendar, Lightbulb, ExternalLink, Star, Tag, Home } from 'lucide-react';

const hotels = [
  {
    name: 'CoolRooms Palacio Villapanés – 5 GL',
    address: 'C. Santiago, 31, Casco Antiguo, 41003 Sevilla, Spain',
    mapUrl: 'https://maps.google.com/?q=CoolRooms+Palacio+Villapanes+Sevilla',
    stars: 5
  },
  {
    name: 'Hotel One Shot Conde Torrejón',
    address: 'C. Conde de Torrejón, 9, Casco Antiguo, 41003 Sevilla, Spain',
    mapUrl: 'https://maps.google.com/?q=Hotel+One+Shot+Conde+Torrejon+Sevilla',
    stars: 4,
    offer: {
      discount: '-10%',
      code: 'BODA171026',
      validity: 'Valable jusqu\'au 17.09.26'
    }
  },
  {
    name: 'Legado Magdalena Hotel',
    address: 'C. San Pablo, 22, Casco Antiguo, 41001 Sevilla, Spain',
    mapUrl: 'https://maps.google.com/?q=Legado+Magdalena+Hotel+Sevilla',
    stars: 4,
    offer: {
      discount: '-17%',
      code: 'GELLYCHRIS',
      validity: 'Minimum 2 nuits'
    }
  },
  {
    name: 'Aquitania Home Suites',
    address: 'C. Martín Villa, 3, Casco Antiguo, 41003 Sevilla, Spain',
    mapUrl: 'https://maps.google.com/?q=Aquitania+Home+Suites+Sevilla',
    stars: 4
  },
  {
    name: 'Hotel Alcázar Sevilla',
    address: 'Av. de Menéndez Pelayo, 10, 41004 Sevilla, Spain',
    mapUrl: 'https://maps.google.com/?q=Hotel+Alcazar+Sevilla',
    stars: 3
  },
  {
    name: 'B&B HOTEL Sevilla Bormujos',
    address: 'C. el Aceitunillo, 52, 41930 Bormujos, Sevilla, Spain',
    mapUrl: 'https://maps.google.com/?q=B%26B+HOTEL+Sevilla+Bormujos',
    stars: 2
  },
  {
    name: 'AIRBNB',
    isAirbnb: true,
    mapUrl: 'https://fr.airbnb.ch/s/S%C3%A9ville--Espagne/homes?refinement_paths%5B%5D=%2Fhomes&place_id=ChIJkWK-FBFsEg0RSFb-HGIY8DQ&location_bb=QhW9%2BcC8bkFCFULJwMCxbA%3D%3D&acp_id=wedding-invite-gc&date_picker_type=calendar&checkin=2026-10-17&checkout=2026-10-19&adults=1&search_type=autocomplete_click'
  }
];

const StarRating = ({ stars }) => (
  <div className="flex items-center gap-0.5">
    {[...Array(stars)].map((_, i) => (
      <Star key={i} className="w-3 h-3 fill-[#e17c3b] text-[#e17c3b]" />
    ))}
  </div>
);

export const AccommodationPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="accommodation-page">
      {/* Header */}
      <section className="py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-display text-5xl md:text-7xl mb-6 animate-fade-in text-[#e17c3b]">Hébergement</h1>
          <p className="text-[#8d5f3f]/70 text-lg max-w-2xl mx-auto animate-fade-in delay-100">
            Conseils pour votre séjour à Séville
          </p>
        </div>
      </section>

      {/* Info Cards - Removed "Options disponibles" */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Location Card */}
            <div 
              className="bg-[#fcf6f1] border border-[#e8ddd4] p-8 rounded-2xl hover:shadow-lg transition-all duration-300 animate-slide-up text-center"
              data-testid="location-card"
            >
              <div className="flex flex-col items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#e17c3b]/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#e17c3b]" />
                </div>
                <h2 className="font-display text-2xl text-[#8d5f3f]">Lieu du mariage</h2>
              </div>
              <p className="text-[#8d5f3f]/80 leading-relaxed">
                Le lieu de réception se trouve à <strong className="text-[#8d5f3f]">20-30 minutes</strong> de Séville.
                Nous vous recommandons de choisir un hébergement dans le <strong className="text-[#8d5f3f]">centre ville</strong> pour profiter pleinement du week-end.
              </p>
            </div>

            {/* Booking Tip Card - Now Brown */}
            <div 
              className="bg-[#8d5f3f] p-8 rounded-2xl hover:shadow-lg transition-all duration-300 animate-slide-up delay-100 text-center"
              data-testid="booking-card"
            >
              <div className="flex flex-col items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <h2 className="font-display text-2xl text-white">Réservation</h2>
              </div>
              <p className="text-white/90 leading-relaxed">
                <strong className="text-white">Réservez tôt !</strong> Le mois d'octobre est très demandé à Séville. 
                La météo y est idéale et de nombreux événements ont lieu à cette période.
              </p>
            </div>

            {/* Note Card */}
            <div 
              className="bg-[#e17c3b]/5 border border-[#e17c3b]/20 p-8 rounded-2xl animate-slide-up delay-200 text-center"
              data-testid="note-card"
            >
              <div className="flex flex-col items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#e17c3b]/10 flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-[#e17c3b]" />
                </div>
                <h2 className="font-display text-2xl text-[#8d5f3f]">À noter</h2>
              </div>
              <p className="text-[#8d5f3f]/80 leading-relaxed">
                Ces suggestions sont <strong className="text-[#8d5f3f]">facultatives et non obligatoires</strong>. 
                Vous êtes libres de choisir l'hébergement qui vous convient le mieux.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hotel Suggestions - Sorted by stars, no highlighting */}
      <section className="py-16 px-6 md:px-12" data-testid="hotel-suggestions">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl text-center mb-12 text-[#8d5f3f]">Suggestions d'hôtels</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {hotels.map((hotel, index) => (
              <div 
                key={hotel.name}
                className="bg-[#fcf6f1] border border-[#e8ddd4] rounded-2xl p-6 hover:shadow-lg transition-all duration-300 animate-slide-up text-center"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`hotel-card-${index}`}
              >
                {/* Stars or Airbnb icon */}
                {hotel.isAirbnb ? (
                  <div className="flex items-center justify-center gap-2 text-[#e17c3b] text-sm mb-3">
                    <Home className="w-4 h-4" />
                    <span className="font-medium">Location</span>
                  </div>
                ) : hotel.stars && (
                  <div className="flex justify-center mb-3">
                    <StarRating stars={hotel.stars} />
                  </div>
                )}
                
                <h3 className="font-display text-lg text-[#8d5f3f] mb-2">{hotel.name}</h3>
                
                {hotel.address && (
                  <p className="text-[#6b8e4e] text-sm mb-4 leading-relaxed">{hotel.address}</p>
                )}
                
                {/* Special Offer */}
                {hotel.offer && (
                  <div className="bg-[#e17c3b]/10 border border-[#e17c3b]/20 rounded-xl p-3 mb-4">
                    <div className="flex items-center justify-center gap-2 text-[#e17c3b] font-medium mb-1">
                      <Tag className="w-4 h-4" />
                      <span>Offre Spéciale {hotel.offer.discount}</span>
                    </div>
                    <p className="text-[#8d5f3f] text-sm font-mono">Code : {hotel.offer.code}</p>
                    <p className="text-[#8d5f3f]/60 text-xs">{hotel.offer.validity}</p>
                  </div>
                )}
                
                <a 
                  href={hotel.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#e17c3b] hover:text-[#c96a2f] text-sm font-medium transition-colors"
                  data-testid={`hotel-link-${index}`}
                >
                  <ExternalLink className="w-4 h-4" />
                  {hotel.isAirbnb ? 'Voir les annonces' : 'Voir sur Google Maps'}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom Decorative Images - Hébergement 1 & 2 */}
      <section className="py-8 px-6 md:px-12" data-testid="accommodation-bottom-images">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_wedding-invite-gc/artifacts/fltnr3sk_He%CC%81bergement%201.jpeg"
                alt="Hébergement 1"
                className="w-full h-64 md:h-72 object-cover hover:scale-105 transition-transform duration-500"
                loading="lazy"
                decoding="async"
                data-testid="accommodation-bottom-image-1"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_wedding-invite-gc/artifacts/49kusfyy_He%CC%81bergement%202.jpeg"
                alt="Hébergement 2"
                className="w-full h-64 md:h-72 object-cover hover:scale-105 transition-transform duration-500"
                loading="lazy"
                decoding="async"
                data-testid="accommodation-bottom-image-2"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
